import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as CloudDownloadIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  MonetizationOn as MonetizationOnIcon,
  PriceChange as PriceChangeIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import StoreWorkflowSelector, {
  type StoreWorkflow,
} from "../components/StoreWorkflowSelector";
import CreationProgressModal from "../components/CreationProgressModal";
import AIService from "../services/AIService";
import AppService from "../services/AppService";
import SubscriptionService from "../services/SubscriptionService";
import GooglePlaySubscriptionsWorkflow from "./GooglePlaySubscriptionsWorkflow";
import type { App } from "../types/app";
import type {
  AppStoreSubscriptionPricingResponse,
  AppStoreSubscriptionPeriod,
  AppStoreSubscriptionsCreateRequest,
  AppStoreSubscriptionsCreateResponse,
  AppStoreSubscriptionsValidationResponse,
  StoredAppStoreSubscriptionRecord,
  SubscriptionValidationIssue,
  SubscriptionPricePointOption,
  SubscriptionGroupLocalizationInput,
  SubscriptionItemInput,
  SubscriptionLocalizationInput,
  SubscriptionTemplatePreset,
  AppStoreSubscriptionFreeTrialDuration,
} from "../types/subscription";
import {
  APP_STORE_LISTING_LOCALES,
  filterStoreLocalesBySupport,
  getStoreLocaleDisplayLabel,
  type StoreLocaleSupportFilter,
} from "../utils/storeLocales";

const AI_MODELS = [
  AIService.getDefaultModel(),
  "gemini-2.5-flash-lite",
  "gemini-2.5-pro",
  "gpt-4.1-mini",
  "gpt-4.1",
  "o4-mini",
];

const SUBSCRIPTION_PERIOD_OPTIONS: Array<{
  value: AppStoreSubscriptionPeriod;
  label: string;
}> = [
  { value: "ONE_WEEK", label: "1 week" },
  { value: "ONE_MONTH", label: "1 month" },
  { value: "TWO_MONTHS", label: "2 months" },
  { value: "THREE_MONTHS", label: "3 months" },
  { value: "SIX_MONTHS", label: "6 months" },
  { value: "ONE_YEAR", label: "1 year" },
];

const TEMPLATE_OPTIONS: Array<{
  value: SubscriptionTemplatePreset;
  label: string;
  description: string;
}> = [
  {
    value: "monthly_yearly",
    label: "Yearly and Monthly",
    description: "Generate monthly and yearly plans with localized copy.",
  },
  {
    value: "yearly_weekly",
    label: "Yearly and Weekly",
    description: "Generate weekly and yearly plans with localized copy.",
  },
  {
    value: "yearly_only",
    label: "Only Yearly",
    description: "Generate one yearly subscription.",
  },
  {
    value: "monthly_only",
    label: "Only Monthly",
    description: "Generate one monthly subscription.",
  },
  {
    value: "custom_selection",
    label: "Custom Selection",
    description: "Choose any combination of weekly, monthly, and yearly plans.",
  },
];

const AI_PLAN_DURATION_OPTIONS: Array<{
  value: AppStoreSubscriptionPeriod;
  label: string;
}> = [
  { value: "ONE_WEEK", label: "1 week" },
  { value: "ONE_MONTH", label: "1 month" },
  { value: "ONE_YEAR", label: "1 year" },
];

const TEMPLATE_PERIODS: Record<
  SubscriptionTemplatePreset,
  AppStoreSubscriptionPeriod[]
> = {
  monthly_yearly: ["ONE_MONTH", "ONE_YEAR"],
  yearly_weekly: ["ONE_WEEK", "ONE_YEAR"],
  yearly_only: ["ONE_YEAR"],
  monthly_only: ["ONE_MONTH"],
  custom_selection: ["ONE_WEEK", "ONE_MONTH", "ONE_YEAR"],
};

const FREE_TRIAL_DURATION_OPTIONS: {
  value: AppStoreSubscriptionFreeTrialDuration;
  label: string;
}[] = [
  { value: "THREE_DAYS", label: "3 Days" },
  { value: "ONE_WEEK", label: "1 Week" },
  { value: "TWO_WEEKS", label: "2 Weeks" },
  { value: "ONE_MONTH", label: "1 Month" },
  { value: "TWO_MONTHS", label: "2 Months" },
  { value: "THREE_MONTHS", label: "3 Months" },
  { value: "SIX_MONTHS", label: "6 Months" },
  { value: "ONE_YEAR", label: "1 Year" },
];

const APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS = 30;
const APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS = 30;
const APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS = 30;
const APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS = 55;
const AI_TEMPLATE_MIN_MAX_TOKENS = 3200;
const AI_TEMPLATE_MAX_MAX_TOKENS = 16000;
const AI_TEMPLATE_RETRY_COUNT = 2;
const AI_TEMPLATE_LOCALE_BATCH_SIZE = 10;

const TERRITORY_OPTIONS = ["Global", "USA", "GBR", "CAN", "AUS", "DEU", "FRA"];
const TERRITORY_CURRENCY_MAP: Record<string, string> = {
  Global: "USD",
  USA: "USD",
  GBR: "GBP",
  CAN: "CAD",
  AUS: "AUD",
  DEU: "EUR",
  FRA: "EUR",
};

type PricingPresetProfile = "starter" | "growth" | "premium";

const APPLE_PRICE_POINT_PRESET_BY_CURRENCY: Record<
  string,
  Record<PricingPresetProfile, string[]>
> = {
  USD: {
    starter: [
      "0.99",
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "6.99",
      "7.99",
      "8.99",
      "9.99",
      "11.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
    ],
    growth: [
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "7.99",
      "9.99",
      "11.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
    ],
    premium: [
      "4.99",
      "7.99",
      "9.99",
      "11.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
      "69.99",
      "79.99",
      "99.99",
    ],
  },
  GBP: {
    starter: [
      "0.99",
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "6.99",
      "7.99",
      "8.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
    ],
    growth: [
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "7.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
    ],
    premium: [
      "4.99",
      "7.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
      "69.99",
      "79.99",
    ],
  },
  EUR: {
    starter: [
      "0.99",
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "6.99",
      "7.99",
      "8.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
    ],
    growth: [
      "1.99",
      "2.99",
      "3.99",
      "4.99",
      "5.99",
      "7.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
    ],
    premium: [
      "4.99",
      "7.99",
      "9.99",
      "10.99",
      "12.99",
      "14.99",
      "17.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
      "69.99",
      "79.99",
    ],
  },
  AUD: {
    starter: [
      "1.49",
      "2.99",
      "4.49",
      "5.99",
      "7.99",
      "8.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "22.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
    ],
    growth: [
      "2.99",
      "4.49",
      "5.99",
      "7.99",
      "8.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
    ],
    premium: [
      "7.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
      "69.99",
      "79.99",
      "89.99",
      "99.99",
    ],
  },
  CAD: {
    starter: [
      "1.29",
      "2.49",
      "3.99",
      "4.99",
      "6.49",
      "7.99",
      "8.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "22.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
    ],
    growth: [
      "2.49",
      "3.99",
      "4.99",
      "6.49",
      "7.99",
      "8.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
    ],
    premium: [
      "6.49",
      "8.99",
      "10.99",
      "12.99",
      "14.99",
      "16.99",
      "19.99",
      "24.99",
      "29.99",
      "34.99",
      "39.99",
      "49.99",
      "59.99",
      "69.99",
      "79.99",
      "99.99",
    ],
  },
};

const buildPresetPricePointId = (territory: string, customerPrice: string) =>
  `preset::${territory.toUpperCase()}::${customerPrice}`;

const inferPricingPresetProfile = (
  monetizationHints: string[],
): PricingPresetProfile => {
  const blob = monetizationHints.join(" ").toLowerCase();
  if (
    blob.includes("enterprise") ||
    blob.includes("premium") ||
    blob.includes("high") ||
    blob.includes("pro")
  ) {
    return "premium";
  }
  if (
    blob.includes("freemium") ||
    blob.includes("trial") ||
    blob.includes("intro") ||
    blob.includes("entry")
  ) {
    return "starter";
  }
  return "growth";
};

const buildHardcodedPricePointOptions = (params: {
  territory: string;
  monetizationHints: string[];
}): SubscriptionPricePointOption["options"] => {
  const territory = params.territory || "Global";
  const currencyCode = TERRITORY_CURRENCY_MAP[territory] || "USD";
  const profile = inferPricingPresetProfile(params.monetizationHints);
  const profileCatalog =
    APPLE_PRICE_POINT_PRESET_BY_CURRENCY[currencyCode] ||
    APPLE_PRICE_POINT_PRESET_BY_CURRENCY.USD;
  const prices = profileCatalog[profile] || profileCatalog.growth;
  return prices.map((customerPrice) => ({
    id: buildPresetPricePointId(territory, customerPrice),
    customerPrice,
    proceeds: "",
  }));
};

const createGroupLocalizationSet = (
  locales: string[],
  seedName = "",
): SubscriptionGroupLocalizationInput[] =>
  locales.map((locale) => ({ locale, name: seedName, customAppName: "" }));

const createSubscriptionLocalizationSet = (
  locales: string[],
  seedName = "",
): SubscriptionLocalizationInput[] =>
  locales.map((locale) => ({
    locale,
    name: seedName,
    description: "",
    customAppName: "",
  }));

const createSubscriptionDraft = (locales: string[]): SubscriptionItemInput => ({
  clientId: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  referenceName: "",
  productId: "",
  subscriptionPeriod: "ONE_MONTH",
  familySharable: false,
  reviewNote: "",
  groupLevel: undefined,
  localizations: createSubscriptionLocalizationSet(locales),
});

const syncLocalizationSet = (
  existing: SubscriptionLocalizationInput[],
  locales: string[],
): SubscriptionLocalizationInput[] => {
  const byLocale = new Map(existing.map((entry) => [entry.locale, entry]));
  return locales.map(
    (locale) =>
      byLocale.get(locale) || {
        locale,
        name: "",
        description: "",
        customAppName: "",
      },
  );
};

const syncGroupLocalizationSet = (
  existing: SubscriptionGroupLocalizationInput[],
  locales: string[],
  defaultName = "",
): SubscriptionGroupLocalizationInput[] => {
  const byLocale = new Map(existing.map((entry) => [entry.locale, entry]));
  return locales.map(
    (locale) =>
      byLocale.get(locale) || { locale, name: defaultName, customAppName: "" },
  );
};

const sanitizeReferenceToken = (value: string) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

const groupRecordsByDate = (records: StoredAppStoreSubscriptionRecord[]) => {
  const groups = new Map<string, StoredAppStoreSubscriptionRecord[]>();
  records.forEach((record) => {
    const dateKey = record.createdAt.toDateString();
    const current = groups.get(dateKey) || [];
    current.push(record);
    groups.set(dateKey, current);
  });
  return Array.from(groups.entries()).map(([dateKey, items]) => ({
    dateKey,
    items: items.sort(
      (left, right) => right.updatedAt.getTime() - left.updatedAt.getTime(),
    ),
  }));
};

type LocaleIssueDetail = {
  locale: string;
  label: string;
  detail: string;
  issue: SubscriptionValidationIssue;
};

const parseLocaleFromValidationIssueField = (field: string): string | null => {
  const value = String(field || "").trim();
  if (!value) return null;

  const groupMatch = value.match(/^groupLocalizations\.([^.]+)/i);
  if (groupMatch?.[1]) return groupMatch[1];

  const subscriptionMatch = value.match(
    /^subscriptions\.\d+\.localizations\.([^.]+)/i,
  );
  if (subscriptionMatch?.[1]) return subscriptionMatch[1];

  return null;
};

const Subscriptions: React.FC = () => {
  const { user } = useAuth();
  const [storeWorkflow, setStoreWorkflow] =
    useState<StoreWorkflow>("app_store_connect");
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [loadingApps, setLoadingApps] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validating, setValidating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [generatingAIPricing, setGeneratingAIPricing] = useState(false);
  const [phaseTab, setPhaseTab] = useState(0);
  const [flowState, setFlowState] = useState<
    "idle" | "validating" | "creating" | "success"
  >("idle");
  const [selectedLocales, setSelectedLocales] = useState<string[]>(["en-US"]);
  const [localeSupportFilter, setLocaleSupportFilter] =
    useState<StoreLocaleSupportFilter>("common");
  const [localeSearchQuery, setLocaleSearchQuery] = useState("");
  const [templatePreset, setTemplatePreset] =
    useState<SubscriptionTemplatePreset>("monthly_yearly");
  const [selectedPlanDurations, setSelectedPlanDurations] = useState<
    AppStoreSubscriptionPeriod[]
  >(TEMPLATE_PERIODS.monthly_yearly);
  const [aiModel, setAiModel] = useState(AIService.getDefaultModel());
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [pricingTerritory, setPricingTerritory] = useState("Global");
  const [loadingPricePoints, setLoadingPricePoints] = useState(false);
  const [applyingPricing, setApplyingPricing] = useState(false);
  const [pricePointsBySubscriptionId, setPricePointsBySubscriptionId] =
    useState<Record<string, SubscriptionPricePointOption["options"]>>({});
  const [selectedPricePointIds, setSelectedPricePointIds] = useState<
    Record<string, string>
  >({});
  const [selectedFreeTrials, setSelectedFreeTrials] = useState<
    Record<
      string,
      { enabled: boolean; duration: AppStoreSubscriptionFreeTrialDuration }
    >
  >({});
  const [pricingResult, setPricingResult] =
    useState<AppStoreSubscriptionPricingResponse | null>(null);
  const [groupReferenceName, setGroupReferenceName] = useState("");
  const [groupLocalizations, setGroupLocalizations] = useState<
    SubscriptionGroupLocalizationInput[]
  >(createGroupLocalizationSet(["en-US"]));
  const [subscriptions, setSubscriptions] = useState<SubscriptionItemInput[]>([
    createSubscriptionDraft(["en-US"]),
  ]);
  const [validationResult, setValidationResult] =
    useState<AppStoreSubscriptionsValidationResponse | null>(null);
  const [createValidationIssues, setCreateValidationIssues] = useState<
    SubscriptionValidationIssue[]
  >([]);
  const [createValidationMessage, setCreateValidationMessage] = useState("");
  const [bulkFixingValidationIssues, setBulkFixingValidationIssues] =
    useState(false);
  const [fixingLocaleIssueKey, setFixingLocaleIssueKey] = useState<
    string | null
  >(null);
  const [autoValidateAfterGeneration, setAutoValidateAfterGeneration] =
    useState(false);
  const [creationResult, setCreationResult] =
    useState<AppStoreSubscriptionsCreateResponse | null>(null);
  const [storedRecords, setStoredRecords] = useState<
    StoredAppStoreSubscriptionRecord[]
  >([]);
  const [expandedSections, setExpandedSections] = useState({
    setup: true,
    locales: true,
    ai: true,
    group: true,
    subscriptions: true,
    review: true,
    pricing: true,
    records: true,
  });

  useToastStateSync({ error, success });

  const selectableApps = useMemo(
    () =>
      apps.filter((app) => app.platform === "ios" || app.platform === "both"),
    [apps],
  );
  const selectedApp = useMemo(
    () => selectableApps.find((app) => app.appId === selectedAppId) || null,
    [selectableApps, selectedAppId],
  );
  const filteredLocales = useMemo(() => {
    const normalizedSearch = localeSearchQuery.trim().toLowerCase();
    return filterStoreLocalesBySupport(
      APP_STORE_LISTING_LOCALES,
      localeSupportFilter,
    ).filter(
      (locale) =>
        !normalizedSearch ||
        locale.code.toLowerCase().includes(normalizedSearch) ||
        locale.name.toLowerCase().includes(normalizedSearch),
    );
  }, [localeSearchQuery, localeSupportFilter]);
  const groupedRecords = useMemo(
    () => groupRecordsByDate(storedRecords),
    [storedRecords],
  );
  const selectedRecord = useMemo(
    () =>
      storedRecords.find((record) => record.recordId === selectedRecordId) ||
      null,
    [storedRecords, selectedRecordId],
  );

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const load = async () => {
      try {
        setLoadingApps(true);
        const userApps = await AppService.getUserApps(user.email);
        if (!mounted) return;
        setApps(userApps);
      } catch (loadError) {
        console.error(loadError);
        if (mounted) setError("Failed to load registered apps.");
      } finally {
        if (mounted) setLoadingApps(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !selectedAppId) {
      setStoredRecords([]);
      return;
    }
    let mounted = true;
    const load = async () => {
      try {
        setLoadingRecords(true);
        const records = await SubscriptionService.listStoredAppSubscriptions(
          selectedAppId,
          user.email,
        );
        if (mounted) setStoredRecords(records);
      } catch (loadError) {
        console.error(loadError);
        if (mounted) setError("Failed to load stored subscription records.");
      } finally {
        if (mounted) setLoadingRecords(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [selectedAppId, user]);

  useEffect(() => {
    setGroupLocalizations((current) =>
      syncGroupLocalizationSet(
        current,
        selectedLocales,
        selectedApp?.name || "",
      ),
    );
    setSubscriptions((current) =>
      current.map((item) => ({
        ...item,
        localizations: syncLocalizationSet(item.localizations, selectedLocales),
      })),
    );
  }, [selectedLocales, selectedApp?.name]);

  useEffect(() => {
    if (!storedRecords.length) {
      setSelectedRecordId("");
      setPricePointsBySubscriptionId({});
      setSelectedPricePointIds({});
      return;
    }
    setSelectedRecordId((current) =>
      current && storedRecords.some((record) => record.recordId === current)
        ? current
        : storedRecords[0].recordId,
    );
  }, [storedRecords]);

  const requestPayload =
    useMemo<AppStoreSubscriptionsCreateRequest | null>(() => {
      if (!selectedAppId) return null;
      return {
        appId: selectedAppId,
        groupReferenceName: groupReferenceName.trim(),
        groupLocalizations: groupLocalizations.map(
          ({ locale, name, customAppName }) => ({
            locale,
            name: name.trim(),
            customAppName: customAppName?.trim() || "",
          }),
        ),
        subscriptions: subscriptions.map((item) => ({
          referenceName: item.referenceName.trim(),
          productId: item.productId.trim(),
          subscriptionPeriod: item.subscriptionPeriod,
          familySharable: item.familySharable,
          reviewNote: item.reviewNote?.trim() || "",
          ...(typeof item.groupLevel === "number" && item.groupLevel > 0
            ? { groupLevel: item.groupLevel }
            : {}),
          localizations: item.localizations.map(
            ({ locale, name, description }) => ({
              locale,
              name: name.trim(),
              description: description.trim(),
            }),
          ),
        })),
      };
    }, [selectedAppId, groupReferenceName, groupLocalizations, subscriptions]);

  const effectiveValidationIssues =
    validationResult?.validationIssues || createValidationIssues;
  const effectiveValidationMessage =
    validationResult?.message || createValidationMessage;
  const blockingIssues =
    effectiveValidationIssues.filter(
      (issue) => issue.severity === "error",
    ) || [];
  const hasValidationFeedback =
    Boolean(validationResult) ||
    createValidationIssues.length > 0 ||
    Boolean(createValidationMessage);
  const validationErrorCount = effectiveValidationIssues.filter(
    (issue) => issue.severity === "error",
  ).length;
  const validationWarningCount = effectiveValidationIssues.filter(
    (issue) => issue.severity === "warning",
  ).length;
  const localeIssueSummaries = useMemo(() => {
    const byLocale = new Map<string, LocaleIssueDetail[]>();
    const getLengthMeta = (
      issue: SubscriptionValidationIssue,
      locale: string,
    ): { count: number; limit: number; label: string } | null => {
      const field = String(issue.field || "").trim();
      const groupFieldMatch = field.match(
        /^groupLocalizations\.[^.]+\.(name|customAppName)$/i,
      );
      if (groupFieldMatch) {
        const fieldKey = groupFieldMatch[1];
        const groupLoc = groupLocalizations.find((item) => item.locale === locale);
        const value =
          fieldKey === "name"
            ? String(groupLoc?.name || "")
            : String(groupLoc?.customAppName || "");
        const limit =
          fieldKey === "name"
            ? APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS
            : APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS;
        return {
          count: value.length,
          limit,
          label: fieldKey === "name" ? "Group Name" : "Custom App Name",
        };
      }

      const subscriptionFieldMatch = field.match(
        /^subscriptions\.(\d+)\.localizations\.[^.]+\.(name|description)$/i,
      );
      if (!subscriptionFieldMatch) return null;

      const subscriptionIndex = Number(subscriptionFieldMatch[1]);
      const fieldKey = subscriptionFieldMatch[2];
      const subscription = subscriptions[subscriptionIndex];
      if (!subscription) return null;
      const localization = subscription.localizations.find(
        (item) => item.locale === locale,
      );
      const value =
        fieldKey === "name"
          ? String(localization?.name || "")
          : String(localization?.description || "");
      const limit =
        fieldKey === "name"
          ? APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS
          : APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS;
      return {
        count: value.length,
        limit,
        label:
          fieldKey === "name"
            ? `Subscription ${subscriptionIndex + 1} Name`
            : `Subscription ${subscriptionIndex + 1} Description`,
      };
    };

    effectiveValidationIssues.forEach((issue) => {
      const locale = parseLocaleFromValidationIssueField(issue.field);
      if (!locale) return;

      const lengthMeta = getLengthMeta(issue, locale);
      const detail = lengthMeta
        ? `${lengthMeta.label} +${Math.max(lengthMeta.count - lengthMeta.limit, 0)} (${lengthMeta.count}/${lengthMeta.limit})`
        : issue.message;
      const detailEntry: LocaleIssueDetail = {
        locale,
        label: lengthMeta?.label || issue.field,
        detail,
        issue,
      };
      const current = byLocale.get(locale) || [];
      current.push(detailEntry);
      byLocale.set(locale, current);
    });

    return Array.from(byLocale.entries())
      .map(([locale, issues]) => ({
        locale,
        issues,
        errorCount: issues.filter((entry) => entry.issue.severity === "error")
          .length,
        warningCount: issues.filter(
          (entry) => entry.issue.severity === "warning",
        ).length,
      }))
      .sort((left, right) =>
        getStoreLocaleDisplayLabel(left.locale).localeCompare(
          getStoreLocaleDisplayLabel(right.locale),
        ),
      );
  }, [effectiveValidationIssues, groupLocalizations, subscriptions]);

  const resetReviewState = () => {
    setValidationResult(null);
    setCreateValidationIssues([]);
    setCreateValidationMessage("");
    setCreationResult(null);
    setSuccess("");
  };

  const focusLocaleIssueEditor = (locale: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      group: true,
      subscriptions: true,
    }));
    window.requestAnimationFrame(() => {
      const target = document.getElementById(`subscription-locale-${locale}`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const runAiValidationFix = async (
    issuesToFix: SubscriptionValidationIssue[],
    contextLabel: string,
  ) => {
    if (!requestPayload) {
      setError("Select an app before fixing validation issues.");
      return;
    }
    if (issuesToFix.length === 0) {
      setSuccess("No blocking validation issues to fix.");
      return;
    }

    const fixSchema = {
      type: "object",
      additionalProperties: false,
      properties: {
        groupReferenceName: { type: "string" },
        groupLocalizations: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              locale: { type: "string" },
              name: { type: "string" },
              customAppName: { type: "string" },
            },
            required: ["locale", "name", "customAppName"],
          },
        },
        subscriptions: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              referenceName: { type: "string" },
              productId: { type: "string" },
              subscriptionPeriod: { type: "string" },
              familySharable: { type: "boolean" },
              reviewNote: { type: "string" },
              groupLevel: { type: "number" },
              localizations: {
                type: "array",
                items: {
                  type: "object",
                  additionalProperties: false,
                  properties: {
                    locale: { type: "string" },
                    name: { type: "string" },
                    description: { type: "string" },
                  },
                  required: ["locale", "name", "description"],
                },
              },
            },
            required: [
              "referenceName",
              "productId",
              "subscriptionPeriod",
              "familySharable",
              "localizations",
            ],
          },
        },
      },
      required: ["groupReferenceName", "groupLocalizations", "subscriptions"],
    };

    try {
      setBulkFixingValidationIssues(true);
      setError("");
      setSuccess("");
      const fixPrompt = [
        "You are fixing an App Store Connect subscription payload.",
        "Return JSON only and keep structure aligned to the schema.",
        "Fix ALL validation errors while preserving intent and locales.",
        "",
        "Hard constraints:",
        `- Group localization name max ${APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS} chars.`,
        `- Group custom app name max ${APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS} chars.`,
        `- Subscription localization name max ${APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS} chars.`,
        `- Subscription localization description max ${APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS} chars.`,
        "- Keep subscriptionPeriod values to supported constants.",
        "- Do not remove subscriptions unless impossible.",
        "",
        "Current payload JSON:",
        JSON.stringify(requestPayload, null, 2),
        "",
        "Validation issues to fix:",
        JSON.stringify(issuesToFix, null, 2),
      ].join("\n");

      const fixedPayload = await AIService.generateJSON<{
        groupReferenceName: string;
        groupLocalizations: Array<{
          locale: string;
          name: string;
          customAppName?: string;
        }>;
        subscriptions: Array<{
          referenceName: string;
          productId: string;
          subscriptionPeriod: AppStoreSubscriptionPeriod;
          familySharable: boolean;
          reviewNote?: string;
          groupLevel?: number;
          localizations: Array<{
            locale: string;
            name: string;
            description: string;
          }>;
        }>;
      }>(fixPrompt, {
        model: aiModel,
        temperature: 0.15,
        maxTokens: 5200,
        structuredOutputName: "subscription_bulk_fix",
        structuredOutputSchema: fixSchema,
      });

      applyAiSuggestion(fixedPayload);
      setValidationResult(null);
      setCreateValidationIssues([]);
      setCreateValidationMessage("");
      setSuccess(
        `Applied AI ${contextLabel} for ${issuesToFix.length} validation issue${
          issuesToFix.length === 1 ? "" : "s"
        }. Re-validate before creating.`,
      );
    } catch (bulkFixError) {
      console.error(bulkFixError);
      setError(
        bulkFixError instanceof Error
          ? bulkFixError.message
          : "Failed to bulk-fix validation issues.",
      );
    } finally {
      setBulkFixingValidationIssues(false);
    }
  };

  const handleBulkFixValidationIssues = async () => {
    await runAiValidationFix(blockingIssues, "bulk fix");
  };

  const handleFixLocaleValidationIssues = async (locale: string) => {
    const localeIssues = blockingIssues.filter(
      (issue) => parseLocaleFromValidationIssueField(issue.field) === locale,
    );
    if (localeIssues.length === 0) {
      setSuccess(
        `${getStoreLocaleDisplayLabel(locale)} has no blocking validation issues.`,
      );
      return;
    }

    try {
      setFixingLocaleIssueKey(locale);
      await runAiValidationFix(
        localeIssues,
        `fix for ${getStoreLocaleDisplayLabel(locale)}`,
      );
      focusLocaleIssueEditor(locale);
    } finally {
      setFixingLocaleIssueKey(null);
    }
  };

  const updateSubscription = (
    clientId: string,
    updater: (current: SubscriptionItemInput) => SubscriptionItemInput,
  ) => {
    setSubscriptions((current) =>
      current.map((item) =>
        item.clientId === clientId ? updater(item) : item,
      ),
    );
  };

  const addSubscription = () => {
    setSubscriptions((current) => [
      ...current,
      createSubscriptionDraft(selectedLocales),
    ]);
  };

  const removeSubscription = (clientId: string) => {
    setSubscriptions((current) =>
      current.filter((item) => item.clientId !== clientId),
    );
  };

  const applyAiSuggestion = (payload: any) => {
    const nextGroupReferenceName = sanitizeReferenceToken(
      payload?.groupReferenceName || "premium_access",
    );
    const nextGroupLocalizations = syncGroupLocalizationSet(
      (Array.isArray(payload?.groupLocalizations)
        ? payload.groupLocalizations
        : []
      ).map((entry: any) => ({
        locale: String(entry?.locale || "").trim(),
        name: String(entry?.name || "").trim(),
        customAppName: String(entry?.customAppName || "").trim(),
      })),
      selectedLocales,
      selectedApp?.name || "",
    );
    const nextSubscriptions = (
      Array.isArray(payload?.subscriptions) ? payload.subscriptions : []
    )
      .map((item: any, index: number) => ({
        clientId: `sub_${Date.now()}_${index}`,
        referenceName: sanitizeReferenceToken(
          item?.referenceName || `plan_${index + 1}`,
        ),
        productId: String(item?.productId || "").trim(),
        subscriptionPeriod: String(
          item?.subscriptionPeriod || "ONE_MONTH",
        ).trim() as AppStoreSubscriptionPeriod,
        familySharable: Boolean(item?.familySharable),
        reviewNote: String(item?.reviewNote || "").trim(),
        groupLevel:
          Number.isFinite(Number(item?.groupLevel)) &&
          Number(item?.groupLevel) > 0
            ? Math.floor(Number(item.groupLevel))
            : undefined,
        localizations: syncLocalizationSet(
          (Array.isArray(item?.localizations) ? item.localizations : []).map(
            (entry: any) => ({
              locale: String(entry?.locale || "").trim(),
              name: String(entry?.name || "").trim(),
              description: String(entry?.description || "").trim(),
              customAppName: "",
            }),
          ),
          selectedLocales,
        ),
      }))
      .filter(
        (item: SubscriptionItemInput) => item.referenceName || item.productId,
      );

    if (!nextSubscriptions.length) {
      throw new Error("AI did not return any valid subscriptions.");
    }

    setGroupReferenceName(nextGroupReferenceName);
    setGroupLocalizations(nextGroupLocalizations);
    setSubscriptions(nextSubscriptions);
    setExpandedSections((prev) => ({
      ...prev,
      group: true,
      subscriptions: true,
      review: true,
    }));
  };

  const handleGenerateFromTemplate = async () => {
    if (!selectedApp) {
      setError("Select an app before using AI generation.");
      return;
    }
    if (selectedLocales.length === 0) {
      setError("Select at least one locale before using AI generation.");
      return;
    }

    const expectedPlans = AI_PLAN_DURATION_OPTIONS.map(
      (option) => option.value,
    ).filter((period) => selectedPlanDurations.includes(period));

    if (!expectedPlans.length) {
      setError("Select at least one plan duration before using AI generation.");
      return;
    }

    const coreSchema = {
      type: "object",
      additionalProperties: false,
      properties: {
        groupReferenceName: { type: "string" },
        groupDisplayName: { type: "string" },
        groupCustomAppName: { type: "string" },
        subscriptions: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              referenceName: { type: "string" },
              productId: { type: "string" },
              subscriptionPeriod: { type: "string" },
              familySharable: { type: "boolean" },
              reviewNote: { type: "string" },
              groupLevel: { type: "number" },
              baseName: { type: "string" },
              baseDescription: { type: "string" },
            },
            required: [
              "referenceName",
              "productId",
              "subscriptionPeriod",
              "familySharable",
              "baseName",
              "baseDescription",
            ],
          },
        },
      },
      required: ["groupReferenceName", "groupDisplayName", "subscriptions"],
    };

    const localizationSchema = {
      type: "object",
      additionalProperties: false,
      properties: {
        groupLocalizations: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              locale: { type: "string" },
              name: { type: "string" },
              customAppName: { type: "string" },
            },
            required: ["locale", "name", "customAppName"],
          },
        },
        subscriptions: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              referenceName: { type: "string" },
              productId: { type: "string" },
              subscriptionPeriod: { type: "string" },
              familySharable: { type: "boolean" },
              reviewNote: { type: "string" },
              groupLevel: { type: "number" },
              localizations: {
                type: "array",
                items: {
                  type: "object",
                  additionalProperties: false,
                  properties: {
                    locale: { type: "string" },
                    name: { type: "string" },
                    description: { type: "string" },
                  },
                  required: ["locale", "name", "description"],
                },
              },
            },
            required: [
              "referenceName",
              "localizations",
            ],
          },
        },
      },
      required: ["groupLocalizations", "subscriptions"],
    };

    try {
      setError("");
      setSuccess("");
      setGeneratingAI(true);
      resetReviewState();
      const bundleHintRaw = String(
        selectedApp.storeId || selectedApp.appId || selectedApp.name || "app",
      ).toLowerCase();
      const bundlePrefix =
        bundleHintRaw
          .replace(/[^a-z0-9.]+/g, ".")
          .replace(/\.+/g, ".")
          .replace(/^\.|\.$/g, "") || "app";

      const corePrompt = [
        "Create App Store Connect subscription metadata core for an iOS app.",
        "Return JSON only matching the schema.",
        `App name: ${selectedApp.name}`,
        `App category: ${selectedApp.category}`,
        `Bundle identifier or hint: ${selectedApp.storeId || selectedApp.iosStoreUrl || selectedApp.appId}`,
        `Selected template: ${templatePreset}`,
        `Required subscription periods: ${expectedPlans.join(", ")}`,
        "Requirements:",
        "- Suggest one group reference name in snake_case.",
        "- Suggest product IDs using bundle prefix when practical.",
        "- Include concise baseName/baseDescription in English for each plan (baseDescription max 55 chars).",
        "- groupLevel should be 1 for the cheaper tier and 2 for the higher tier when there are two plans.",
        "- familySharable should default to false.",
        "- reviewNote can be short.",
        "- Keep productId and referenceName URL-safe and no spaces.",
        "- Output valid JSON only.",
      ].join("\n");

      let coreResult: any = null;
      let coreError: unknown = null;
      for (let attempt = 1; attempt <= AI_TEMPLATE_RETRY_COUNT; attempt += 1) {
        try {
          coreResult = await AIService.generateJSON<any>(corePrompt, {
            model: aiModel,
            temperature: attempt === 1 ? 0.3 : 0.2,
            maxTokens: 1800 + (attempt - 1) * 1000,
            structuredOutputSchema: coreSchema,
            structuredOutputName: "subscription_template_core",
          });
          coreError = null;
          break;
        } catch (error) {
          coreError = error;
        }
      }
      if (coreError) {
        throw coreError;
      }

      const expectedPeriodSet = new Set(expectedPlans);
      const coreSubscriptionsRaw = Array.isArray(coreResult?.subscriptions)
        ? coreResult.subscriptions
        : [];
      const byPeriod = new Map<AppStoreSubscriptionPeriod, any>();

      coreSubscriptionsRaw.forEach((item: any) => {
        const period = String(
          item?.subscriptionPeriod || "ONE_MONTH",
        ).trim() as AppStoreSubscriptionPeriod;
        if (!expectedPeriodSet.has(period) || byPeriod.has(period)) return;
        byPeriod.set(period, {
          referenceName: sanitizeReferenceToken(
            String(item?.referenceName || `${period.toLowerCase()}_plan`),
          ),
          productId: String(item?.productId || "")
            .trim()
            .replace(/\s+/g, "")
            .slice(0, 100),
          subscriptionPeriod: period,
          familySharable: Boolean(item?.familySharable),
          reviewNote: String(item?.reviewNote || "").trim(),
          groupLevel:
            Number.isFinite(Number(item?.groupLevel)) &&
            Number(item?.groupLevel) > 0
              ? Math.floor(Number(item.groupLevel))
              : undefined,
          baseName: String(item?.baseName || "").trim(),
          baseDescription: String(item?.baseDescription || "").trim(),
        });
      });

      expectedPlans.forEach((period, index) => {
        if (byPeriod.has(period)) return;
        const periodToken = period.toLowerCase();
        byPeriod.set(period, {
          referenceName: sanitizeReferenceToken(`${periodToken}_plan`),
          productId: `${bundlePrefix}.sub.${periodToken}`.slice(0, 100),
          subscriptionPeriod: period,
          familySharable: false,
          reviewNote: "",
          groupLevel:
            expectedPlans.length === 2
              ? index === 0
                ? 1
                : 2
              : undefined,
          baseName: `${period.replace("_", " ").toLowerCase()} plan`,
          baseDescription: "Unlock premium access.",
        });
      });

      const normalizedCoreSubscriptions = expectedPlans
        .map((period) => byPeriod.get(period))
        .filter(Boolean)
        .map((item: any) => ({
          ...item,
          referenceName: sanitizeReferenceToken(item.referenceName),
          productId:
            String(item.productId || "")
              .toLowerCase()
              .replace(/[^a-z0-9._-]+/g, "")
              .slice(0, 100) || `${bundlePrefix}.sub.plan`,
          baseName:
            String(item.baseName || "").trim() ||
            String(item.referenceName || "Premium").replace(/_/g, " "),
          baseDescription:
            String(item.baseDescription || "").trim() || "Unlock premium access.",
        }));

      if (!normalizedCoreSubscriptions.length) {
        throw new Error("AI did not return any valid subscriptions.");
      }

      const chunkedLocales: string[][] = [];
      for (
        let index = 0;
        index < selectedLocales.length;
        index += AI_TEMPLATE_LOCALE_BATCH_SIZE
      ) {
        chunkedLocales.push(
          selectedLocales.slice(index, index + AI_TEMPLATE_LOCALE_BATCH_SIZE),
        );
      }

      const groupBaseName = String(
        coreResult?.groupDisplayName || selectedApp.name || "Premium",
      ).trim();
      const groupBaseCustomAppName = String(
        coreResult?.groupCustomAppName || "",
      ).trim();
      const productsForPrompt = normalizedCoreSubscriptions
        .map(
          (subscription) =>
            `- referenceName: ${subscription.referenceName}; productId: ${subscription.productId}; subscriptionPeriod: ${subscription.subscriptionPeriod}; baseName: ${subscription.baseName}; baseDescription: ${subscription.baseDescription}`,
        )
        .join("\n");

      const requestLocalizationBatch = async (localeBatch: string[]) => {
        const localizationPrompt = [
          "Localize App Store subscription metadata for provided locales.",
          `App name: ${selectedApp.name}`,
          `App category: ${selectedApp.category}`,
          `Target locales: ${localeBatch.join(", ")}`,
          `Group base name: ${groupBaseName}`,
          `Group base custom app name: ${groupBaseCustomAppName}`,
          "Output localization entries for each provided locale and each provided subscription referenceName.",
          "Use exactly the given locale codes and referenceName values.",
          "Length limits: group name <= 30, custom app name <= 30, subscription name <= 30, description <= 55.",
          "Subscriptions:",
          productsForPrompt,
          "Return JSON only matching schema.",
        ].join("\n");

        let batchResult: any = null;
        let batchError: unknown = null;
        const estimatedOutputTokens =
          700 + localeBatch.length * normalizedCoreSubscriptions.length * 70;
        const suggestedMaxTokens = Math.min(
          AI_TEMPLATE_MAX_MAX_TOKENS,
          Math.max(AI_TEMPLATE_MIN_MAX_TOKENS, estimatedOutputTokens),
        );
        for (let attempt = 1; attempt <= AI_TEMPLATE_RETRY_COUNT; attempt += 1) {
          try {
            batchResult = await AIService.generateJSON<any>(localizationPrompt, {
              model: aiModel,
              temperature: attempt === 1 ? 0.3 : 0.2,
              maxTokens: Math.min(
                AI_TEMPLATE_MAX_MAX_TOKENS,
                suggestedMaxTokens + (attempt - 1) * 1800,
              ),
              structuredOutputSchema: localizationSchema,
              structuredOutputName: "subscription_template_localizations",
            });
            batchError = null;
            break;
          } catch (error) {
            batchError = error;
          }
        }
        if (batchError) {
          throw batchError;
        }
        return batchResult;
      };

      const groupLocalizationsByLocale = new Map<
        string,
        { name: string; customAppName: string }
      >();
      selectedLocales.forEach((locale) => {
        groupLocalizationsByLocale.set(locale, {
          name: groupBaseName,
          customAppName: groupBaseCustomAppName,
        });
      });

      const subscriptionLocalizationsByReference = new Map<
        string,
        Map<string, { name: string; description: string }>
      >();
      normalizedCoreSubscriptions.forEach((subscription) => {
        const localeMap = new Map<string, { name: string; description: string }>();
        selectedLocales.forEach((locale) => {
          localeMap.set(locale, {
            name: subscription.baseName,
            description: subscription.baseDescription,
          });
        });
        subscriptionLocalizationsByReference.set(subscription.referenceName, localeMap);
      });

      for (const localeBatch of chunkedLocales) {
        let batchResult: any;
        try {
          batchResult = await requestLocalizationBatch(localeBatch);
        } catch (batchError) {
          const fallbackGroupLocalizations: any[] = [];
          const fallbackSubscriptionsByReference = new Map<string, any[]>();

          for (const locale of localeBatch) {
            const singleResult = await requestLocalizationBatch([locale]);
            if (Array.isArray(singleResult?.groupLocalizations)) {
              fallbackGroupLocalizations.push(...singleResult.groupLocalizations);
            }
            const singleSubs = Array.isArray(singleResult?.subscriptions)
              ? singleResult.subscriptions
              : [];
            singleSubs.forEach((entry: any) => {
              const referenceName = sanitizeReferenceToken(
                String(entry?.referenceName || ""),
              );
              if (!referenceName) return;
              if (!fallbackSubscriptionsByReference.has(referenceName)) {
                fallbackSubscriptionsByReference.set(referenceName, []);
              }
              const localizations = Array.isArray(entry?.localizations)
                ? entry.localizations
                : [];
              fallbackSubscriptionsByReference
                .get(referenceName)!
                .push(...localizations);
            });
          }

          batchResult = {
            groupLocalizations: fallbackGroupLocalizations,
            subscriptions: Array.from(fallbackSubscriptionsByReference.entries()).map(
              ([referenceName, localizations]) => ({
                referenceName,
                localizations,
              }),
            ),
          };
          console.warn(
            "Locale batch fallback to single-locale generation.",
            localeBatch,
            batchError,
          );
        }

        const groupEntries = Array.isArray(batchResult?.groupLocalizations)
          ? batchResult.groupLocalizations
          : [];
        groupEntries.forEach((entry: any) => {
          const locale = String(entry?.locale || "").trim();
          if (!locale || !selectedLocales.includes(locale)) return;
          groupLocalizationsByLocale.set(locale, {
            name: String(entry?.name || groupBaseName).trim() || groupBaseName,
            customAppName: String(entry?.customAppName || "").trim(),
          });
        });

        const subscriptionEntries = Array.isArray(batchResult?.subscriptions)
          ? batchResult.subscriptions
          : [];
        subscriptionEntries.forEach((entry: any) => {
          const referenceName = sanitizeReferenceToken(
            String(entry?.referenceName || ""),
          );
          if (!referenceName) return;
          const localeMap = subscriptionLocalizationsByReference.get(referenceName);
          if (!localeMap) return;
          const localizations = Array.isArray(entry?.localizations)
            ? entry.localizations
            : [];
          localizations.forEach((locEntry: any) => {
            const locale = String(locEntry?.locale || "").trim();
            if (!locale || !selectedLocales.includes(locale)) return;
            const current = localeMap.get(locale) || {
              name: "",
              description: "",
            };
            localeMap.set(locale, {
              name: String(locEntry?.name || current.name).trim() || current.name,
              description:
                String(locEntry?.description || current.description).trim() ||
                current.description,
            });
          });
        });
      }

      const collatedPayload = {
        groupReferenceName: sanitizeReferenceToken(
          coreResult?.groupReferenceName || "premium_access",
        ),
        groupLocalizations: selectedLocales.map((locale) => {
          const entry = groupLocalizationsByLocale.get(locale) || {
            name: groupBaseName,
            customAppName: groupBaseCustomAppName,
          };
          return {
            locale,
            name: entry.name,
            customAppName: entry.customAppName,
          };
        }),
        subscriptions: normalizedCoreSubscriptions.map((subscription) => {
          const localeMap = subscriptionLocalizationsByReference.get(
            subscription.referenceName,
          );
          return {
            referenceName: subscription.referenceName,
            productId: subscription.productId,
            subscriptionPeriod: subscription.subscriptionPeriod,
            familySharable: subscription.familySharable,
            reviewNote: subscription.reviewNote,
            ...(typeof subscription.groupLevel === "number"
              ? { groupLevel: subscription.groupLevel }
              : {}),
            localizations: selectedLocales.map((locale) => {
              const localized = localeMap?.get(locale) || {
                name: subscription.baseName,
                description: subscription.baseDescription,
              };
              return {
                locale,
                name: localized.name || subscription.baseName,
                description: localized.description || subscription.baseDescription,
              };
            }),
          };
        }),
      };

      applyAiSuggestion(collatedPayload);
      setValidationResult(null);
      setCreateValidationIssues([]);
      setCreateValidationMessage("");
      setAutoValidateAfterGeneration(true);
      setSuccess(
        `AI populated the ${TEMPLATE_OPTIONS.find((item) => item.value === templatePreset)?.label || "selected"} template. Running validation...`,
      );
    } catch (aiError) {
      console.error(aiError);
      setError(
        aiError instanceof Error
          ? aiError.message
          : "Failed to generate subscription metadata with AI.",
      );
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleValidate = async () => {
    if (!requestPayload) {
      setError("Select an app before validating subscriptions.");
      return;
    }

    const runValidation = async (
      payload: AppStoreSubscriptionsCreateRequest,
      options?: { successMessage?: string },
    ) => {
      try {
        setError("");
        setSuccess("");
        setValidating(true);
        setFlowState("validating");
        setCreationResult(null);
        setCreateValidationIssues([]);
        setCreateValidationMessage("");
        const result =
          await SubscriptionService.validateAppStoreConnectSubscriptions(
            payload,
          );
        setValidationResult(result);
        setFlowState("idle");
        if (result.validationIssues.some((issue) => issue.severity === "error")) {
          setError("Fix the subscription validation issues before creating.");
        } else {
          setSuccess(options?.successMessage || "Subscription payload validated.");
        }
      } catch (validateError) {
        console.error(validateError);
        setFlowState("idle");
        setError(
          validateError instanceof Error
            ? validateError.message
            : "Failed to validate subscriptions.",
        );
      } finally {
        setValidating(false);
      }
    };

    await runValidation(requestPayload);
  };

  useEffect(() => {
    if (!autoValidateAfterGeneration) return;
    if (!requestPayload) return;

    let cancelled = false;

    const runAutoValidation = async () => {
      try {
        setError("");
        setSuccess("Generated draft ready. Running validation...");
        setValidating(true);
        setFlowState("validating");
        setCreationResult(null);
        setCreateValidationIssues([]);
        setCreateValidationMessage("");

        const result =
          await SubscriptionService.validateAppStoreConnectSubscriptions(
            requestPayload,
          );
        if (cancelled) return;

        setValidationResult(result);
        setFlowState("idle");
        if (result.validationIssues.some((issue) => issue.severity === "error")) {
          setError("Fix the subscription validation issues before creating.");
        } else {
          setSuccess(
            "Generation completed and validation passed automatically.",
          );
        }
      } catch (validateError) {
        if (cancelled) return;
        console.error(validateError);
        setFlowState("idle");
        setError(
          validateError instanceof Error
            ? validateError.message
            : "Failed to validate subscriptions.",
        );
      } finally {
        if (!cancelled) {
          setValidating(false);
          setAutoValidateAfterGeneration(false);
        }
      }
    };

    runAutoValidation();

    return () => {
      cancelled = true;
    };
  }, [autoValidateAfterGeneration, requestPayload]);

  const handleCreate = async () => {
    if (!requestPayload) {
      setError("Select an app before creating subscriptions.");
      return;
    }
    try {
      setError("");
      setSuccess("");
      setCreating(true);
      setFlowState("creating");
      setCreateValidationIssues([]);
      setCreateValidationMessage("");
      const result =
        await SubscriptionService.createAppStoreConnectSubscriptions(
          requestPayload,
        );
      setCreationResult(result);
      setFlowState("success");
      setSuccess("Subscriptions created in App Store Connect.");
      if (user?.email) {
        const records = await SubscriptionService.listStoredAppSubscriptions(
          selectedAppId,
          user.email,
        );
        setStoredRecords(records);
      }
    } catch (createError) {
      console.error(createError);
      setFlowState("idle");
      const responseData = (createError as any)?.responseData as
        | {
            error?: string;
            message?: string;
            validationIssues?: SubscriptionValidationIssue[];
          }
        | undefined;
      const responseIssues = Array.isArray(responseData?.validationIssues)
        ? responseData?.validationIssues?.filter(
            (issue) =>
              issue &&
              typeof issue.field === "string" &&
              typeof issue.message === "string" &&
              (issue.severity === "error" || issue.severity === "warning"),
          ) || []
        : [];
      if (responseIssues.length > 0) {
        setCreateValidationIssues(responseIssues);
        setCreateValidationMessage(
          String(
            responseData?.error ||
              responseData?.message ||
              "Create endpoint rejected the payload. Fix validation issues and retry.",
          ).trim(),
        );
        setExpandedSections((prev) => ({ ...prev, review: true }));
      }
      setError(
        createError instanceof Error
          ? createError.message
          : "Failed to create subscriptions.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleLoadRecordIntoEditor = (
    record: StoredAppStoreSubscriptionRecord,
  ) => {
    setPhaseTab(0);
    setSelectedLocales(
      Array.from(
        new Set([
          ...record.group.localizations.map((entry) => entry.locale),
          ...record.subscriptions.flatMap((item) =>
            item.localizations.map((entry) => entry.locale),
          ),
        ]),
      ),
    );
    setGroupReferenceName(record.group.referenceName);
    setGroupLocalizations(
      record.group.localizations.map((entry) => ({
        locale: entry.locale,
        name: entry.name,
        customAppName: "",
      })),
    );
    setSubscriptions(
      record.subscriptions.map((item, index) => ({
        clientId: `record_${record.recordId}_${index}`,
        referenceName: item.referenceName,
        productId: item.productId,
        subscriptionPeriod: item.subscriptionPeriod,
        familySharable: item.familySharable,
        reviewNote: "",
        groupLevel:
          typeof item.groupLevel === "number" ? item.groupLevel : undefined,
        localizations: item.localizations.map((entry) => ({
          locale: entry.locale,
          name: entry.name,
          description: "",
          customAppName: "",
        })),
      })),
    );
    setSuccess("Loaded saved subscription record into the editor.");
  };

  const handleDeleteRecord = async (recordId: string) => {
    try {
      setError("");
      await SubscriptionService.deleteStoredAppSubscriptionRecord(recordId);
      setStoredRecords((current) =>
        current.filter((record) => record.recordId !== recordId),
      );
      if (selectedRecordId === recordId) {
        setSelectedRecordId("");
      }
      setSuccess("Saved subscription record deleted.");
    } catch (deleteError) {
      console.error(deleteError);
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Failed to delete the subscription record.",
      );
    }
  };

  const handleLoadPricePoints = async () => {
    if (!selectedRecord) {
      setError("Select a saved record before loading price points.");
      return;
    }
    try {
      setError("");
      setLoadingPricePoints(true);
      setPricingResult(null);

      const nextMap: Record<string, SubscriptionPricePointOption["options"]> =
        {};
      const nextSelected: Record<string, string> = {};
      const monetizationHints = Array.isArray(
        selectedApp?.researchContext?.monetization,
      )
        ? selectedApp?.researchContext?.monetization || []
        : [];
      selectedRecord.subscriptions.forEach((subscription) => {
        const options = buildHardcodedPricePointOptions({
          territory: pricingTerritory,
          monetizationHints,
        });
        nextMap[subscription.id] = options;
        if (options[0]?.id) {
          nextSelected[subscription.id] = options[0].id;
        }
      });
      setPricePointsBySubscriptionId(nextMap);
      setSelectedPricePointIds(nextSelected);
      setSuccess(
        `Loaded hardcoded common Apple price points for ${pricingTerritory} (${TERRITORY_CURRENCY_MAP[pricingTerritory] || "USD"}).`,
      );
    } catch (pricePointError) {
      console.error(pricePointError);
      setError(
        pricePointError instanceof Error
          ? pricePointError.message
          : "Failed to load subscription price points.",
      );
    } finally {
      setLoadingPricePoints(false);
    }
  };

  const handleApplyPricing = async () => {
    if (!selectedRecord) {
      setError("Select a saved record before applying pricing.");
      return;
    }
    const prices = selectedRecord.subscriptions
      .map((item) => {
        const freeTrialConfig = selectedFreeTrials[item.id];
        return {
          subscriptionId: item.id,
          pricePointId: selectedPricePointIds[item.id],
          freeTrial: freeTrialConfig?.enabled ? freeTrialConfig : undefined,
        };
      })
      .filter((item) => item.subscriptionId && item.pricePointId);
    if (!prices.length) {
      setError(
        "Load price points and select at least one price before applying pricing.",
      );
      return;
    }
    try {
      setError("");
      setApplyingPricing(true);
      const result = await SubscriptionService.configureSubscriptionPricing({
        appId: selectedAppId,
        recordId: selectedRecord.recordId,
        baseTerritory: pricingTerritory,
        prices,
      });
      setPricingResult(result);
      if (user?.email) {
        const records = await SubscriptionService.listStoredAppSubscriptions(
          selectedAppId,
          user.email,
        );
        setStoredRecords(records);
      }
      setSuccess(result.message);
    } catch (pricingError) {
      console.error(pricingError);
      setError(
        pricingError instanceof Error
          ? pricingError.message
          : "Failed to apply subscription pricing.",
      );
    } finally {
      setApplyingPricing(false);
    }
  };

  const handleGenerateAIPricing = async () => {
    if (!selectedApp || !selectedRecord) return;
    try {
      setError("");
      setGeneratingAIPricing(true);
      const prompt = `You are a monetization expert. A mobile app developer is setting up subscription pricing for their app.
App Name: ${selectedApp.name}
App Description/Context: This app is in the ${selectedApp.category || "General"} category.
Target Territory: ${pricingTerritory}

The developer has configured the following subscription products:
${selectedRecord.subscriptions.map((sub) => `- ID: ${sub.id}, Name: ${sub.name || sub.referenceName}, Period: ${sub.subscriptionPeriod}`).join("\n")}

Suggest the most optimal customer price for each subscription based on the app context and standard market pricing for this territory.
You MUST pick one of the available prices exactly as listed below.

Available customer prices per subscription:
${selectedRecord.subscriptions
  .map((sub) => {
    const options = pricePointsBySubscriptionId[sub.id] || [];
    const uniquePrices = Array.from(
      new Set(options.map((o) => o.customerPrice)),
    ).sort((a, b) => parseFloat(a) - parseFloat(b));
    return `${sub.id}: ${uniquePrices.join(", ")}`;
  })
  .join("\n\n")}

Return a compact JSON object mapping each subscription ID to the exact chosen customer price string (e.g. "9.99").
`;

      const schema = {
        type: "object",
        properties: {
          recommendedPrices: {
            type: "object",
            additionalProperties: { type: "string" },
            description:
              "Map of subscription ID to the recommended customer price string.",
          },
        },
        required: ["recommendedPrices"],
      };

      const result = await AIService.generateJSON<any>(prompt, {
        model: aiModel,
        temperature: 0.3,
        maxTokens: 1000,
        structuredOutputSchema: schema,
        structuredOutputName: "pricing_suggestion",
      });

      if (result?.recommendedPrices) {
        const nextSelected = { ...selectedPricePointIds };
        Object.entries(result.recommendedPrices).forEach(
          ([subId, targetPrice]) => {
            const options = pricePointsBySubscriptionId[subId] || [];
            const matchedOption =
              options.find((o) => o.customerPrice === targetPrice) ||
              options.find(
                (o) =>
                  parseFloat(o.customerPrice) ===
                  parseFloat(String(targetPrice)),
              ) ||
              options[0];
            if (matchedOption) {
              nextSelected[subId] = matchedOption.id;
            }
          },
        );
        setSelectedPricePointIds(nextSelected);
        setSuccess("AI successfully suggested optimal price points.");
      } else {
        throw new Error("AI returned invalid format.");
      }
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to generate AI pricing suggestions.",
      );
    } finally {
      setGeneratingAIPricing(false);
    }
  };

  if (loadingApps) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const structureFormHidden = flowState === "creating";
  const showCreationProgressModal =
    storeWorkflow === "app_store_connect" && creating;

  return (
    <Box sx={{ width: 1, maxWidth: "none", px: { xs: 1, md: 2 }, pb: 4 }}>
      <CreationProgressModal
        open={showCreationProgressModal}
        storeLabel="App Store Connect"
        title="Creating subscriptions"
        description="We are creating the subscription group and products in App Store Connect. Please keep this page open until the operation completes."
      />
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(37,99,235,0.08) 55%, rgba(15,23,42,0.02) 100%)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <MonetizationOnIcon sx={{ fontSize: 36, color: "success.main" }} />
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
              >
                Subscriptions
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Phase 1 creates the subscription group and products. Phase 2
                uses the saved structure for pricing support.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label={selectedApp ? `App: ${selectedApp.name}` : "Select an app"}
            />
            <Chip label={`Locales: ${selectedLocales.length}`} />
            <Chip label={`Draft subscriptions: ${subscriptions.length}`} />
            <Chip label={`Stored records: ${storedRecords.length}`} />
          </Stack>
          <StoreWorkflowSelector
            value={storeWorkflow}
            onChange={(value) => {
              setStoreWorkflow(value);
              setError("");
              setSuccess("");
            }}
          />
        </Stack>
      </Paper>

      {storeWorkflow === "google_play" ? (
        <GooglePlaySubscriptionsWorkflow />
      ) : null}

      {storeWorkflow === "app_store_connect" ? (
        <Card sx={{ borderRadius: 3, mb: 2.5 }}>
          <CardContent sx={{ pb: 1 }}>
            <Tabs
              value={phaseTab}
              onChange={(_event, value) => setPhaseTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Phase 1: Structure" />
              <Tab label="Phase 2: Pricing" />
            </Tabs>
          </CardContent>
        </Card>
      ) : null}

      {storeWorkflow === "app_store_connect" && phaseTab === 0 ? (
        <>
          <Collapse
            in={!structureFormHidden && hasValidationFeedback}
            timeout={350}
            unmountOnExit
          >
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 2.5 },
                mb: 2.5,
                borderRadius: 3,
                borderColor:
                  validationErrorCount > 0 ? "error.light" : "warning.light",
                background:
                  validationErrorCount > 0
                    ? "linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(248,113,113,0.03) 100%)"
                    : "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(251,191,36,0.03) 100%)",
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Validation Issues
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {effectiveValidationMessage || "Review and resolve issues before creating."}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      color={validationErrorCount > 0 ? "error" : "default"}
                      label={`${validationErrorCount} error${
                        validationErrorCount === 1 ? "" : "s"
                      }`}
                    />
                    <Chip
                      color={validationWarningCount > 0 ? "warning" : "default"}
                      label={`${validationWarningCount} warning${
                        validationWarningCount === 1 ? "" : "s"
                      }`}
                    />
                    <Chip label={`Total: ${effectiveValidationIssues.length}`} />
                  </Stack>
                </Stack>
                {bulkFixingValidationIssues ? (
                  <Fade in={bulkFixingValidationIssues} timeout={250}>
                    <Box>
                      <LinearProgress sx={{ borderRadius: 999, height: 8, mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Bulk fixing issues with AI...
                      </Typography>
                    </Box>
                  </Fade>
                ) : null}
                {localeIssueSummaries.length > 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      borderColor:
                        localeIssueSummaries.length > 0
                          ? "warning.light"
                          : "success.light",
                      bgcolor:
                        localeIssueSummaries.length > 0
                          ? "rgba(237,108,2,0.06)"
                          : "rgba(46,125,50,0.06)",
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            Listing Overview
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Review locale-level subscription compliance before creating.
                          </Typography>
                        </Box>
                        <Chip
                          color="warning"
                          variant="outlined"
                          label={`${localeIssueSummaries.length} locale${
                            localeIssueSummaries.length === 1 ? "" : "s"
                          } with issues`}
                        />
                      </Stack>
                      <Stack spacing={1}>
                        {localeIssueSummaries.map((item) => {
                          const localeBlockingIssues = item.issues.filter(
                            (entry) => entry.issue.severity === "error",
                          );
                          return (
                            <Paper
                              key={`locale-issue-${item.locale}`}
                              variant="outlined"
                              sx={{
                                p: 1.2,
                                borderRadius: 2,
                                bgcolor: "rgba(255,255,255,0.8)",
                                borderColor: "rgba(237,108,2,0.25)",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {getStoreLocaleDisplayLabel(item.locale)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    color={item.errorCount > 0 ? "error" : "warning"}
                                    label={`${item.issues.length} issue${
                                      item.issues.length === 1 ? "" : "s"
                                    }`}
                                    variant="outlined"
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    startIcon={
                                      fixingLocaleIssueKey === item.locale ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : (
                                        <EditIcon fontSize="small" />
                                      )
                                    }
                                    disabled={
                                      bulkFixingValidationIssues ||
                                      creating ||
                                      validating ||
                                      !requestPayload ||
                                      localeBlockingIssues.length === 0 ||
                                      Boolean(
                                        fixingLocaleIssueKey &&
                                          fixingLocaleIssueKey !== item.locale,
                                      )
                                    }
                                    onClick={() =>
                                      handleFixLocaleValidationIssues(item.locale)
                                    }
                                    sx={{ textTransform: "none", borderRadius: 999 }}
                                  >
                                    {fixingLocaleIssueKey === item.locale
                                      ? "Fixing..."
                                      : "Fix issue with AI"}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => focusLocaleIssueEditor(item.locale)}
                                    sx={{ textTransform: "none", borderRadius: 999 }}
                                  >
                                    Open locale
                                  </Button>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {item.issues
                                    .map((entry) => entry.detail)
                                    .filter(Boolean)
                                    .slice(0, 3)
                                    .join(" · ")}
                                </Typography>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}
                <Stack spacing={1}>
                  {effectiveValidationIssues.map((issue, index) => (
                    <Fade
                      in={hasValidationFeedback}
                      timeout={220 + index * 40}
                      key={`top_issue_${issue.field}_${index}`}
                    >
                      <Alert
                        severity={issue.severity === "warning" ? "warning" : "error"}
                        sx={{ mb: 0 }}
                      >
                        <strong>{issue.field}</strong>: {issue.message}
                      </Alert>
                    </Fade>
                  ))}
                </Stack>
                {blockingIssues.length > 0 ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ pt: 0.5 }}
                  >
                    <Button
                      variant="contained"
                      startIcon={
                        bulkFixingValidationIssues ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <AutoAwesomeIcon />
                        )
                      }
                      disabled={
                        bulkFixingValidationIssues ||
                        creating ||
                        validating ||
                        !requestPayload
                      }
                      onClick={handleBulkFixValidationIssues}
                    >
                      {bulkFixingValidationIssues
                        ? "Bulk Fixing..."
                        : "Bulk Fix Issues With AI"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleValidate}
                      disabled={validating || creating || !selectedAppId}
                    >
                      Re-Validate
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
          </Collapse>

          {structureFormHidden ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(59,130,246,0.08) 100%)",
                mb: 2.5,
              }}
            >
              <Stack spacing={2.5} alignItems="flex-start">
                <Chip color="success" label="Creating In App Store Connect" />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Provisioning subscription structure
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 760 }}
                >
                  The setup panels are hidden while the create request is in
                  progress so the active App Store Connect operation stays
                  clear.
                </Typography>
                <Box sx={{ width: "100%", maxWidth: 760 }}>
                  <LinearProgress />
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    label={
                      selectedApp
                        ? `App: ${selectedApp.name}`
                        : "No app selected"
                    }
                  />
                  <Chip label={`Locales: ${selectedLocales.length}`} />
                  <Chip label={`Subscriptions: ${subscriptions.length}`} />
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {!structureFormHidden ? (
            <Stack spacing={2.5}>
              <Accordion
                expanded={expandedSections.setup}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({ ...prev, setup: expanded }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    1. Main Setup
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Registered app</InputLabel>
                        <Select
                          value={selectedAppId}
                          label="Registered app"
                          onChange={(event) =>
                            setSelectedAppId(String(event.target.value))
                          }
                        >
                          <MenuItem value="">
                            <em>Choose your app</em>
                          </MenuItem>
                          {selectableApps.map((app) => (
                            <MenuItem key={app.appId} value={app.appId}>
                              {app.name} •{" "}
                              {app.storeId || app.iosStoreUrl || app.appId}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Monetization template</InputLabel>
                        <Select
                          value={templatePreset}
                          label="Monetization template"
                          onChange={(event) => {
                            const nextTemplate = event.target
                              .value as SubscriptionTemplatePreset;
                            setTemplatePreset(nextTemplate);
                            setSelectedPlanDurations(
                              TEMPLATE_PERIODS[nextTemplate],
                            );
                            resetReviewState();
                          }}
                        >
                          {TEMPLATE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>AI model</InputLabel>
                        <Select
                          value={aiModel}
                          label="AI model"
                          onChange={(event) =>
                            setAiModel(String(event.target.value))
                          }
                        >
                          {Array.from(new Set(AI_MODELS)).map((model) => (
                            <MenuItem key={model} value={model}>
                              {model}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Plan durations for AI generation
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {AI_PLAN_DURATION_OPTIONS.map((option) => {
                          const checked = selectedPlanDurations.includes(
                            option.value,
                          );
                          return (
                            <Chip
                              key={option.value}
                              label={option.label}
                              color={checked ? "primary" : "default"}
                              variant={checked ? "filled" : "outlined"}
                              onClick={() => {
                                setSelectedPlanDurations((current) => {
                                  const next = current.includes(option.value)
                                    ? current.filter(
                                        (item) => item !== option.value,
                                      )
                                    : [...current, option.value];
                                  return AI_PLAN_DURATION_OPTIONS.map(
                                    (item) => item.value,
                                  ).filter((period) => next.includes(period));
                                });
                                resetReviewState();
                              }}
                            />
                          );
                        })}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Choose one or more durations (weekly, monthly, yearly).
                        This applies to AI draft generation only.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        {
                          TEMPLATE_OPTIONS.find(
                            (item) => item.value === templatePreset,
                          )?.description
                        }
                      </Alert>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.locales}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    locales: expanded,
                  }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    2. Target Locales
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 1.2, flexWrap: "wrap" }}
                  >
                    <ToggleButtonGroup
                      value={localeSupportFilter}
                      exclusive
                      size="small"
                      sx={{ flexWrap: "wrap", gap: 1, mr: "auto" }}
                      onChange={(
                        _event,
                        value: StoreLocaleSupportFilter | null,
                      ) => {
                        if (value) setLocaleSupportFilter(value);
                      }}
                    >
                      <ToggleButton value="common">Common to both</ToggleButton>
                      <ToggleButton value="app_store">Apple only</ToggleButton>
                      <ToggleButton value="all">All locales</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small"
                      label="Find locale"
                      placeholder="Type locale name or code"
                      value={localeSearchQuery}
                      onChange={(event) =>
                        setLocaleSearchQuery(event.target.value)
                      }
                      sx={{ minWidth: { xs: "100%", md: 240 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setSelectedLocales(
                          Array.from(
                            new Set([
                              ...selectedLocales,
                              ...filteredLocales.map((locale) => locale.code),
                            ]),
                          ),
                        )
                      }
                    >
                      Select Visible
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        setSelectedLocales((prev) =>
                          prev.filter(
                            (code) =>
                              !filteredLocales.some(
                                (locale) => locale.code === code,
                              ),
                          ),
                        )
                      }
                    >
                      Clear Visible
                    </Button>
                  </Box>
                  <FormGroup
                    sx={{
                      maxHeight: 240,
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 1.25,
                    }}
                  >
                    {filteredLocales.length === 0 ? (
                      <Alert severity="info">
                        No locales match the current filter.
                      </Alert>
                    ) : (
                      filteredLocales.map((locale) => (
                        <FormControlLabel
                          key={locale.code}
                          control={
                            <Checkbox
                              checked={selectedLocales.includes(locale.code)}
                              onChange={() =>
                                setSelectedLocales((prev) =>
                                  prev.includes(locale.code)
                                    ? prev.filter(
                                        (item) => item !== locale.code,
                                      )
                                    : [...prev, locale.code],
                                )
                              }
                            />
                          }
                          label={`${locale.flag} ${locale.name} (${locale.code})`}
                        />
                      ))
                    )}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.ai}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({ ...prev, ai: expanded }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    3. AI Populate Structure
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      Use AI to suggest the group reference name, product IDs,
                      and localized names and descriptions for all selected
                      locales.
                    </Typography>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="contained"
                        startIcon={
                          generatingAI ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AutoAwesomeIcon />
                          )
                        }
                        disabled={
                          generatingAI ||
                          !selectedApp ||
                          selectedLocales.length === 0
                        }
                        onClick={handleGenerateFromTemplate}
                      >
                        {generatingAI ? "Generating..." : "Generate With AI"}
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => {
                          setGroupReferenceName("");
                          setGroupLocalizations(
                            createGroupLocalizationSet(
                              selectedLocales,
                              selectedApp?.name || "",
                            ),
                          );
                          setSubscriptions([
                            createSubscriptionDraft(selectedLocales),
                          ]);
                          resetReviewState();
                        }}
                      >
                        Reset Draft
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          ) : null}
        </>
      ) : (
        <></>
      )}
      {storeWorkflow === "app_store_connect" && phaseTab === 0 ? (
        <>
          {!structureFormHidden ? (
            <Stack spacing={2.5} sx={{ mt: 2.5 }}>
              <Accordion
                expanded={expandedSections.group}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({ ...prev, group: expanded }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    4. Subscription Group
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Subscription group reference name"
                        value={groupReferenceName}
                        onChange={(event) => {
                          resetReviewState();
                          setGroupReferenceName(event.target.value);
                        }}
                        placeholder="premium_access"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    {groupLocalizations.map((entry, index) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        key={entry.locale}
                        id={`subscription-locale-${entry.locale}`}
                      >
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 3, height: "100%" }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1.5 }}
                          >
                            {getStoreLocaleDisplayLabel(entry.locale)}
                          </Typography>
                          <Stack spacing={1.5}>
                            <TextField
                              label="Group display name"
                              value={entry.name}
                              onChange={(event) => {
                                resetReviewState();
                                setGroupLocalizations((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, name: event.target.value }
                                      : item,
                                  ),
                                );
                              }}
                              fullWidth
                              error={
                                entry.name.length >
                                APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS
                              }
                              inputProps={{
                                maxLength:
                                  APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS,
                              }}
                              helperText={`${entry.name.length}/${APP_STORE_SUBSCRIPTION_GROUP_NAME_MAX_CHARS}`}
                            />
                            <TextField
                              label="Custom app name (optional)"
                              value={entry.customAppName || ""}
                              onChange={(event) => {
                                resetReviewState();
                                setGroupLocalizations((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? {
                                          ...item,
                                          customAppName: event.target.value,
                                        }
                                      : item,
                                  ),
                                );
                              }}
                              fullWidth
                              error={
                                (entry.customAppName || "").length >
                                APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS
                              }
                              inputProps={{
                                maxLength:
                                  APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS,
                              }}
                              helperText={`${(entry.customAppName || "").length}/${APP_STORE_SUBSCRIPTION_GROUP_CUSTOM_APP_NAME_MAX_CHARS}`}
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.subscriptions}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    subscriptions: expanded,
                  }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    5. Subscription Items
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2.5}>
                    {subscriptions.map((subscription, index) => (
                      <Card
                        key={subscription.clientId}
                        variant="outlined"
                        sx={{ borderRadius: 3 }}
                      >
                        <CardContent>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 2 }}
                          >
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Subscription {index + 1}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Edit the product and its localized public copy.
                              </Typography>
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() =>
                                removeSubscription(subscription.clientId)
                              }
                              disabled={subscriptions.length === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Reference name"
                                value={subscription.referenceName}
                                onChange={(event) => {
                                  resetReviewState();
                                  updateSubscription(
                                    subscription.clientId,
                                    (current) => ({
                                      ...current,
                                      referenceName: event.target.value,
                                    }),
                                  );
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Product ID"
                                value={subscription.productId}
                                onChange={(event) => {
                                  resetReviewState();
                                  updateSubscription(
                                    subscription.clientId,
                                    (current) => ({
                                      ...current,
                                      productId: event.target.value,
                                    }),
                                  );
                                }}
                                placeholder="com.example.app.pro.monthly"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Billing period</InputLabel>
                                <Select
                                  value={subscription.subscriptionPeriod}
                                  label="Billing period"
                                  onChange={(event) => {
                                    resetReviewState();
                                    updateSubscription(
                                      subscription.clientId,
                                      (current) => ({
                                        ...current,
                                        subscriptionPeriod: event.target
                                          .value as AppStoreSubscriptionPeriod,
                                      }),
                                    );
                                  }}
                                >
                                  {SUBSCRIPTION_PERIOD_OPTIONS.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Group level (optional)"
                                type="number"
                                value={subscription.groupLevel ?? ""}
                                onChange={(event) => {
                                  resetReviewState();
                                  updateSubscription(
                                    subscription.clientId,
                                    (current) => ({
                                      ...current,
                                      groupLevel: event.target.value
                                        ? Number(event.target.value)
                                        : undefined,
                                    }),
                                  );
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={subscription.familySharable}
                                    onChange={(event) => {
                                      resetReviewState();
                                      updateSubscription(
                                        subscription.clientId,
                                        (current) => ({
                                          ...current,
                                          familySharable: event.target.checked,
                                        }),
                                      );
                                    }}
                                  />
                                }
                                label="Family sharable"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Review note (optional)"
                                value={subscription.reviewNote || ""}
                                onChange={(event) => {
                                  resetReviewState();
                                  updateSubscription(
                                    subscription.clientId,
                                    (current) => ({
                                      ...current,
                                      reviewNote: event.target.value,
                                    }),
                                  );
                                }}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                          <Divider sx={{ my: 2.5 }} />
                          <Grid container spacing={2}>
                            {subscription.localizations.map((entry) => (
                              <Grid
                                item
                                xs={12}
                                md={6}
                                key={`${subscription.clientId}_${entry.locale}`}
                              >
                                <Paper
                                  variant="outlined"
                                  sx={{ p: 2, borderRadius: 3, height: "100%" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, mb: 1.5 }}
                                  >
                                    {getStoreLocaleDisplayLabel(entry.locale)}
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    <TextField
                                      label="Display name"
                                      value={entry.name}
                                      onChange={(event) => {
                                        resetReviewState();
                                        updateSubscription(
                                          subscription.clientId,
                                          (current) => ({
                                            ...current,
                                            localizations:
                                              current.localizations.map(
                                                (item) =>
                                                  item.locale === entry.locale
                                                    ? {
                                                        ...item,
                                                        name: event.target
                                                          .value,
                                                      }
                                                    : item,
                                              ),
                                          }),
                                        );
                                      }}
                                      fullWidth
                                      error={
                                        entry.name.length >
                                        APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS
                                      }
                                      inputProps={{
                                        maxLength:
                                          APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS,
                                      }}
                                      helperText={`${entry.name.length}/${APP_STORE_SUBSCRIPTION_LOCALIZATION_NAME_MAX_CHARS}`}
                                    />
                                    <TextField
                                      label="Description"
                                      value={entry.description}
                                      onChange={(event) => {
                                        resetReviewState();
                                        updateSubscription(
                                          subscription.clientId,
                                          (current) => ({
                                            ...current,
                                            localizations:
                                              current.localizations.map(
                                                (item) =>
                                                  item.locale === entry.locale
                                                    ? {
                                                        ...item,
                                                        description:
                                                          event.target.value,
                                                      }
                                                    : item,
                                              ),
                                          }),
                                        );
                                      }}
                                      multiline
                                      minRows={3}
                                      fullWidth
                                      error={
                                        entry.description.length >
                                        APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS
                                      }
                                      inputProps={{
                                        maxLength:
                                          APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS,
                                      }}
                                      helperText={`${entry.description.length}/${APP_STORE_SUBSCRIPTION_LOCALIZATION_DESCRIPTION_MAX_CHARS}`}
                                    />
                                  </Stack>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addSubscription}
                      variant="outlined"
                    >
                      Add another subscription
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.review}
                onChange={(_, expanded) =>
                  setExpandedSections((prev) => ({ ...prev, review: expanded }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    6. Validate and Create
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleValidate}
                        disabled={validating || creating || !selectedAppId}
                      >
                        {validating ? "Validating..." : "Validate Setup"}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={
                          creating ||
                          validating ||
                          !selectedAppId ||
                          selectedLocales.length === 0
                        }
                      >
                        {creating
                          ? "Creating..."
                          : "Create Structure In App Store Connect"}
                      </Button>
                    </Stack>
                    {hasValidationFeedback ? (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 800 }}
                            >
                              Validation result
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {effectiveValidationMessage || "No message."}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {validationResult ? (
                              <>
                                <Chip
                                  label={`Bundle: ${validationResult.packageName}`}
                                />
                                <Chip
                                  label={`Locales: ${validationResult.groupLocales.length}`}
                                />
                                <Chip
                                  label={`Products: ${validationResult.results.length}`}
                                />
                              </>
                            ) : (
                              <Chip
                                color="warning"
                                label="Create endpoint validation failure"
                              />
                            )}
                          </Stack>
                        </Stack>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {effectiveValidationIssues.length === 0 ? (
                            <Alert severity="success">
                              No validation issues detected.
                            </Alert>
                          ) : (
                            effectiveValidationIssues.map(
                              (issue, index) => (
                                <Alert
                                  key={`${issue.field}_${index}`}
                                  severity={
                                    issue.severity === "warning"
                                      ? "warning"
                                      : "error"
                                  }
                                >
                                  <strong>{issue.field}</strong>: {issue.message}
                                </Alert>
                              ),
                            )
                          )}
                        </Stack>
                        {blockingIssues.length > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Button
                              variant="outlined"
                              startIcon={
                                bulkFixingValidationIssues ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <AutoAwesomeIcon />
                                )
                              }
                              disabled={
                                bulkFixingValidationIssues ||
                                creating ||
                                validating ||
                                !requestPayload
                              }
                              onClick={handleBulkFixValidationIssues}
                            >
                              {bulkFixingValidationIssues
                                ? "Bulk Fixing..."
                                : "Bulk Fix Issues With AI"}
                            </Button>
                          </Box>
                        ) : null}
                        {blockingIssues.length === 0 ? (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            Validation passed. After creation, the new record
                            will be stored against this app and become the input
                            for Phase 2 pricing.
                          </Alert>
                        ) : null}
                      </Paper>
                    ) : null}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          ) : null}

          {creationResult ? (
            <Card
              sx={{
                borderRadius: 3,
                mt: 2.5,
                border: "1px solid",
                borderColor: "success.light",
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CheckCircleIcon color="success" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Structure created
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {creationResult.message}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`Bundle: ${creationResult.packageName}`} />
                    <Chip
                      label={`Group: ${creationResult.group.referenceName}`}
                    />
                    <Chip
                      label={`Products: ${creationResult.subscriptions.length}`}
                    />
                    <Chip color="warning" label="Pricing phase pending" />
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<PriceChangeIcon />}
                    onClick={() => setPhaseTab(1)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Continue To Pricing Phase
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : storeWorkflow === "app_store_connect" ? (
        <Stack spacing={2.5} sx={{ mt: 2.5 }}>
          <Accordion
            expanded={expandedSections.pricing}
            onChange={(_, expanded) =>
              setExpandedSections((prev) => ({ ...prev, pricing: expanded }))
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                1. Pricing Phase
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert
                severity="info"
                icon={<PriceChangeIcon fontSize="inherit" />}
              >
                Pricing is the second phase after the subscription structure
                exists. Load a saved record, load curated Apple price points for a
                territory, then apply the selected prices to App Store Connect.
              </Alert>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Saved record</InputLabel>
                    <Select
                      value={selectedRecordId}
                      label="Saved record"
                      onChange={(event) =>
                        setSelectedRecordId(String(event.target.value))
                      }
                    >
                      {storedRecords.map((record) => (
                        <MenuItem key={record.recordId} value={record.recordId}>
                          {record.group.referenceName} • {record.bundleId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Base territory</InputLabel>
                    <Select
                      value={pricingTerritory}
                      label="Base territory"
                      onChange={(event) =>
                        setPricingTerritory(String(event.target.value))
                      }
                    >
                      {TERRITORY_OPTIONS.map((territory) => (
                        <MenuItem key={territory} value={territory}>
                          {territory}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant={
                        Object.keys(pricePointsBySubscriptionId).length > 0
                          ? "outlined"
                          : "contained"
                      }
                      color={
                        Object.keys(pricePointsBySubscriptionId).length > 0
                          ? "primary"
                          : "info"
                      }
                      disabled={!selectedRecord || loadingPricePoints}
                      onClick={handleLoadPricePoints}
                      startIcon={
                        loadingPricePoints ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CloudDownloadIcon />
                        )
                      }
                    >
                      {loadingPricePoints
                        ? "Loading points..."
                        : "Load Common Price Points"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={
                        !selectedRecord ||
                        Object.keys(pricePointsBySubscriptionId).length === 0 ||
                        generatingAIPricing
                      }
                      onClick={handleGenerateAIPricing}
                      startIcon={
                        generatingAIPricing ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <AutoAwesomeIcon />
                        )
                      }
                    >
                      {generatingAIPricing
                        ? "Generating..."
                        : "AI Suggest Pricing"}
                    </Button>
                    <Button
                      variant={applyingPricing ? "outlined" : "contained"}
                      color="success"
                      disabled={
                        !selectedRecord ||
                        applyingPricing ||
                        Object.keys(selectedPricePointIds).length === 0
                      }
                      onClick={handleApplyPricing}
                      startIcon={
                        applyingPricing ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                    >
                      {applyingPricing ? "Applying..." : "Apply Pricing"}
                    </Button>
                  </Stack>
                </Grid>
                {selectedRecord
                  ? selectedRecord.subscriptions.map((item) => {
                      const ftContext = selectedFreeTrials[item.id] || {
                        enabled: false,
                        duration: "ONE_WEEK",
                      };
                      return (
                        <Grid item xs={12} md={6} key={`price_${item.id}`}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800, color: "text.primary" }}
                              >
                                {item.name || item.referenceName}
                              </Typography>
                              <Chip
                                size="small"
                                label={item.subscriptionPeriod.replace(
                                  /_/g,
                                  " ",
                                )}
                                color="primary"
                                variant="outlined"
                              />
                            </Stack>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1.5 }}
                            >
                              {item.productId}
                            </Typography>
                            <FormControl fullWidth>
                              <InputLabel>Price point</InputLabel>
                              <Select
                                value={selectedPricePointIds[item.id] || ""}
                                label="Price point"
                                onChange={(event) =>
                                  setSelectedPricePointIds((current) => ({
                                    ...current,
                                    [item.id]: String(event.target.value),
                                  }))
                                }
                              >
                                {(
                                  pricePointsBySubscriptionId[item.id] || []
                                ).map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {TERRITORY_CURRENCY_MAP[pricingTerritory] || "USD"} {option.customerPrice}
                                    {option.proceeds
                                      ? ` • Proceeds: ${option.proceeds}`
                                      : ""}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={ftContext.enabled}
                                    onChange={(e) =>
                                      setSelectedFreeTrials((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...ftContext,
                                          enabled: e.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                }
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Free Trial Offer
                                  </Typography>
                                }
                              />
                              {ftContext.enabled && (
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 120 }}
                                >
                                  <Select
                                    value={ftContext.duration}
                                    onChange={(e) =>
                                      setSelectedFreeTrials((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...ftContext,
                                          duration: e.target
                                            .value as AppStoreSubscriptionFreeTrialDuration,
                                        },
                                      }))
                                    }
                                  >
                                    {FREE_TRIAL_DURATION_OPTIONS.map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })
                  : null}
                {pricingResult ? (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, mb: 1 }}
                      >
                        {pricingResult.message}
                      </Typography>
                      <Stack spacing={1}>
                        {pricingResult.results.map((item) => (
                          <Alert
                            key={`${item.subscriptionId}_${item.pricePointId}`}
                            severity={
                              item.status === "success" ? "success" : "error"
                            }
                          >
                            {item.productId || item.subscriptionId}:{" "}
                            {item.message}
                          </Alert>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ) : null}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expandedSections.records}
            onChange={(_, expanded) =>
              setExpandedSections((prev) => ({ ...prev, records: expanded }))
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                2. Saved Subscription Records
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loadingRecords ? (
                <Box sx={{ py: 3, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : groupedRecords.length === 0 ? (
                <Alert severity="info">
                  No stored subscription records exist for the selected app yet.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {groupedRecords.map((group) => (
                    <Paper
                      key={group.dateKey}
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          {new Date(group.dateKey).toLocaleDateString(
                            undefined,
                            { year: "numeric", month: "short", day: "numeric" },
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {group.items.length} record
                          {group.items.length === 1 ? "" : "s"}
                        </Typography>
                      </Box>
                      <Stack spacing={1.5} sx={{ p: 2 }}>
                        {group.items.map((record) => (
                          <Paper
                            key={record.recordId}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 3 }}
                          >
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              justifyContent="space-between"
                              spacing={1.5}
                            >
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 800 }}
                                >
                                  {record.group.referenceName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {record.subscriptions.length} subscription
                                  {record.subscriptions.length === 1
                                    ? ""
                                    : "s"}{" "}
                                  • Bundle {record.bundleId}
                                </Typography>
                              </Box>
                              <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                              >
                                <Chip
                                  icon={
                                    record.pricingPhaseStatus === "pending" ? (
                                      <RadioButtonUncheckedIcon />
                                    ) : (
                                      <CheckCircleIcon />
                                    )
                                  }
                                  color={
                                    record.pricingPhaseStatus === "pending"
                                      ? "warning"
                                      : "success"
                                  }
                                  label={
                                    record.pricingPhaseStatus === "pending"
                                      ? "Pricing pending"
                                      : "Pricing configured"
                                  }
                                />
                                <Chip
                                  label={`Updated ${record.updatedAt.toLocaleString()}`}
                                />
                              </Stack>
                            </Stack>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack spacing={1}>
                              {record.subscriptions.map((item) => (
                                <Stack
                                  key={item.id}
                                  direction={{ xs: "column", md: "row" }}
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Box>
                                    <Typography sx={{ fontWeight: 600 }}>
                                      {item.name || item.referenceName}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {item.productId} •{" "}
                                      {item.subscriptionPeriod.replaceAll(
                                        "_",
                                        " ",
                                      )}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`${item.localizations.length} locales`}
                                  />
                                </Stack>
                              ))}
                            </Stack>
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              spacing={1}
                              sx={{ mt: 1.5 }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  handleLoadRecordIntoEditor(record)
                                }
                              >
                                Load Into Editor
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSelectedRecordId(record.recordId);
                                  setPhaseTab(1);
                                }}
                              >
                                Use For Pricing
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={() =>
                                  handleDeleteRecord(record.recordId)
                                }
                              >
                                Delete Record
                              </Button>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      ) : null}
    </Box>
  );
};

export default Subscriptions;
